import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import type {
  ReportParameterDefinition,
  ReportParameterValidValue,
  ReportParameterValue
} from '@/types';
import { useDebounced } from './useDebounced';
import { includesValue, optionFor, unmatchedLabel, withoutValue } from './valueMatching';

/**
 * A picker for a list too long to scroll: type to filter, click to choose.
 *
 * Long is not hypothetical. Head Office on "Any Client Detail (Custom View)" resolves to 5,844
 * entries on MANDW01, so the server sends the first 500 and this asks it for a filtered 500 as the
 * user types. Filtering happens against the server's cached copy of the full list, so it costs a
 * round trip but no warehouse query.
 */
export function SearchablePicker({
  id,
  def,
  options,
  total,
  truncated,
  values,
  rememberedLabel,
  reportPath,
  upstream,
  onChange,
  disabled
}: {
  id: string;
  def: ReportParameterDefinition;
  options: ReportParameterValidValue[];
  total: number;
  truncated: boolean;
  values: string[];
  /** A label seen in an earlier answer, or undefined if this value has never resolved one. */
  rememberedLabel: (value: string) => string | undefined;
  reportPath?: string;
  upstream: ReportParameterValue[];
  onChange: (v: string[]) => void;
  disabled: boolean;
}) {
  const api = useApi();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const debouncedSearch = useDebounced(search, 250);

  // Only when the list arrived capped: a list that fits is filtered here, with no round trip.
  //
  // Deliberately WITHOUT `selected`, unlike the cascade lookup in useResolvedValues. The server
  // honours it on any request, and here that would drop the current selection into a list whose
  // every other row is a search hit — an entry that does not match what the user just typed,
  // presented among the ones that do. It buys nothing either: `options` below is the outer answer,
  // which is pinned, and labelFor consults that first, so a chip keeps its real label throughout a
  // search regardless.
  const { data: searched, isFetching: searchFetching } = useQuery({
    queryKey: ['parameter-values', reportPath, def.name, JSON.stringify(upstream), debouncedSearch],
    queryFn: () => api.resolveParameterValues({
      path: reportPath!,
      values: upstream,
      parameter: def.name,
      search: debouncedSearch
    }),
    enabled: Boolean(reportPath) && truncated && debouncedSearch.trim().length > 0,
    staleTime: 120_000,
    retry: false,
    // Same reason as the cascade: the last set of matches stays on screen while the next is
    // fetched, rather than the list emptying under the cursor between keystrokes.
    placeholderData: keepPreviousData
  });

  const searching = search.trim().length > 0;
  // The debounce counts, same as it does for the cascade: between the keystroke and the request
  // there is a quarter second in which the list is stale and nothing says so.
  const searchPending = searching && truncated && (search !== debouncedSearch || searchFetching);

  const pool = searched?.[0]?.validValues ?? options;
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    // Already filtered upstream when the search went to the server; filtering again would drop
    // nothing but would fight the debounce as the user types.
    if (!term || (truncated && searched)) return pool.slice(0, 200);
    return pool
      .filter(o => o.label.toLowerCase().includes(term) || o.value.toLowerCase().includes(term))
      .slice(0, 200);
  }, [pool, search, truncated, searched]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Checked in that order deliberately: the list just resolved, then the wider search result set
  // it may have come from, then anything remembered from an earlier answer this session — and
  // only once none of those know the value does it get marked as not offered any more. Matched
  // with optionFor/sameValue rather than raw === so a value that is genuinely still there, just
  // spelled with different incidental whitespace than this answer, keeps its real label.
  function labelFor(v: string) {
    return optionFor(v, options)?.label
      ?? optionFor(v, pool)?.label
      ?? rememberedLabel(v)
      ?? unmatchedLabel(v);
  }

  function choose(v: string) {
    if (def.multiValued) {
      onChange(includesValue(values, v) ? withoutValue(values, v) : [...values, v]);
    } else {
      onChange([v]);
      setOpen(false);
      setSearch('');
    }
  }

  return (
    <div className="param-picker" ref={boxRef}>
      {values.length > 0 && (
        <div className="param-chips">
          {values.map(v => (
            <span key={v} className="param-chip">
              {labelFor(v)}
              {!disabled && (
                <button
                  type="button"
                  className="param-chip-x"
                  aria-label={`Remove ${labelFor(v)}`}
                  onClick={() => onChange(withoutValue(values, v))}>
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* The option count lives in the placeholder rather than a note under the box: it is the
          same fact in a line the form was paying for anyway. */}
      <input
        id={id}
        className="input"
        disabled={disabled}
        value={search}
        placeholder={
          values.length > 0
            ? `Change — search ${total.toLocaleString()} options...`
            : `Search ${total.toLocaleString()} options...`
        }
        onFocus={() => setOpen(true)}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); setSearch(''); } }}
      />

      {open && !disabled && (
        <ul className="param-options">
          {/* "No matches" is a result. Until the search has come back it is not one yet. */}
          {shown.length === 0 && (
            <li className="param-option-empty">{searchPending ? 'Searching...' : 'No matches'}</li>
          )}
          {shown.map(o => (
            <li key={o.value}>
              <button
                type="button"
                className={`param-option${includesValue(values, o.value) ? ' is-selected' : ''}`}
                onClick={() => choose(o.value)}>
                {o.label}
                {o.label !== o.value && <span className="param-option-value">{o.value}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Only when the list on screen is a part of the list that exists — a control cannot show
          that it is incomplete, so this is the one count worth a line of its own.

          And only while the user is NOT searching: under a list that is already the results of a
          search, "type to search the rest" is advice to do the thing you have just done. Once the
          search is running the placeholder above still carries the total, which is what this line
          was for. */}
      {truncated && !searching && (
        <div className="param-note">
          Showing {options.length.toLocaleString()} of {total.toLocaleString()} — type to search the rest.
        </div>
      )}
    </div>
  );
}
